<template>
  <div class="fixed sm:absolute left-0 w-full z-50 fixed-footer bottom-0">
    <div class="relative">
      <div class="relative bg-grey-150 z-20 footer-background">
        <div class="footer-view">
          <Transition :name="transitionName">
            <!-- review page -->
            <div v-if="$route.path === '/overview'" key="review" class="inner-footer">
              <DrDottedLoader v-if="loading" />
              <DrButton
                v-else
                :disabled="shouldDisableButton"
                class="footer-button btn-cyan"
                data-cy="btn-review-continue"
                @click="getStatements"
              >
                Continue
              </DrButton>
            </div>
            <!-- confirmation page -->
            <div v-else-if="$route.path === '/quote-confirmation'" key="confirmation" class="inner-footer">
              <DrButton
                v-if="!isPortal"
                :disabled="shouldDisableButton"
                class="footer-button btn-cyan"
                data-cy="btn-setup-cover"
                @click="handleSetUpCover"
              >
                Set up this cover
              </DrButton>
              <DrButton v-else class="footer-button btn-cyan" @click="portalCheckout">Update cover</DrButton>
            </div>
            <!-- product config page -->
            <div v-else-if="$route.path.endsWith('/config')" key="config" class="inner-footer products-footer">
              <!-- estimate is loading -->
              <div v-if="getEstimateLoading">
                <DrDottedLoader />
              </div>
              <!-- estimate exists - means that all inputs were able to be sent -->

              <div v-else-if="getEstimate || appearHereFix">
                <div v-if="!appearHereFix && getEstimate.total !== 0" class="absolute left-0 center-v">
                  <DrDottedLoader v-if="loading" />
                  <div v-else>
                    <div class="text-lg font-bold leading-tight">{{ getEstimate.total | currency(2) }}</div>
                    <div class="text-3xs leading-tight">
                      <template v-if="getPaymentLumpSum">total</template>
                      <template v-if="isPortal">new monthly total</template>
                      <template v-else>per month</template>
                      , inc tax.
                    </div>
                  </div>
                </div>

                <div v-if="!isConfiguredRateActive || appearHereFix">
                  <DrButton
                    :class="{ 'btn-disabled': loading }"
                    class="footer-button btn-cyan"
                    data-cy="btn-add-this-cover"
                    @click="commitRate"
                  >
                    Add this cover
                  </DrButton>
                </div>
                <div v-else>
                  <DrButton
                    :class="{ 'btn-disabled': loading }"
                    class="footer-button btn-cyan"
                    data-cy="btn-add-this-cover"
                    @click="commitRate"
                  >
                    Update this cover
                  </DrButton>
                </div>
              </div>
              <!-- estimate does not exists - means that some inputs still need to be configured -->
              <div v-else class="w-full">
                <div class="text-left font-bold leading-tight py-3">Configure your cover above to add it.</div>
                <span v-if="appearHereFix" />
              </div>
            </div>
            <!-- product list page -->
            <div v-else key="list" class="inner-footer products-footer">
              <div v-if="displayReviewQuote">
                <div class="absolute left-0 center-v">
                  <DrDottedLoader v-if="loading" />
                  <div v-else-if="getMonthlyTotal > 0">
                    <div class="text-lg font-bold leading-tight">{{ getMonthlyTotal | currency(2) }}</div>
                    <div class="text-3xs leading-tight"><template v-if="!getPaymentLumpSum">per month, </template>inc <span v-if="getFee">fee and </span>tax.</div>
                  </div>
                </div>
                <RouterLink
                  to="/overview"
                  :class="{ 'pointer-events-none': loading }"
                  class="relative block no-underline"
                >
                  <DrButton
                    :class="{ 'btn-disabled': loading }"
                    class="footer-button btn btn-cyan"
                    data-cy="btn-review-quote"
                  >
                    Review quote
                  </DrButton>
                </RouterLink>
              </div>
              <div v-else class="w-full text-left font-bold leading-tight py-3">
                {{ isPortal ? 'Add cover above' : 'Add cover above to start your quote.' }}
              </div>
            </div>
          </Transition>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import qs from 'qs'
import rateMixin from '@/mixins/rate'
import axiosChargebee from '@/services/axiosChargebee'
import axiosRatingEngine from '@/services/axiosRatingEngine'
import axiosQuoteApi from '@/services/axiosQuoteApi'

export default {
  mixins: [rateMixin],
  data () {
    return {
      transitionName: '',
      productsForCoupon: '',
      debounceTimeout: null,
      loading: false,
    }
  },
  computed: {
    ...mapGetters({
      premises: 'premises/all',
      getMonthlyTotal: 'getMonthlyTotal',
      getProducts: 'getProducts',
      getAccountActiveRates: 'getAccountActiveRates',
      couponIds: 'couponIds',
      getEstimate: 'getEstimate',
      getEstimateLoading: 'getEstimateLoading',
      getTotal: 'getTotal',
      getFee: 'getFee',
      getSelectedRatesIds: 'getSelectedRatesIds',
      getPaymentLumpSum: 'getPaymentLumpSum',
      getSource: 'getSource',
      shouldUrbanConsent: 'shouldUrbanConsent',
      urbanConsent: 'urbanConsent',
    }),
    isPortal () {
      return !!this.$store.state.portal
    },
    displayReviewQuote () {
      return this.isPortal
        ? this.getSelectedRatesIds.length > this.getAccountActiveRates.length
        : this.getSelectedRatesIds.length >= 1
    },
    isConfiguredRateActive () {
      if (!this.$store.getters.getConfiguredRate) return false

      return this.getRate(this.$store.getters.getConfiguredRate.key)
    },
    chargebeeEstimate: {
      get () {
        return this.$store.getters.getChargebeeEstimate
      },
      set (value) {
        this.$store.commit('updateChargebeeEstimate', value)
      },
    },
    quoteTotal () {
      return this.$store.getters.getTotal
    },
    appearHereFix () {
      // @TODO - this hacky to allow the custome flow for appear here to display the slides after the personal details page
      return this.getSource === 'appear-here' && this.getEstimate === null
    },
    shouldDisableButton () {
      if (!this.shouldUrbanConsent) return false
      return this.shouldUrbanConsent && !this.urbanConsent
    },
  },
  watch: {
    $route (to, from) {
      if (!from.meta.page) {
        this.transitionName = ''
      } else {
        this.transitionName = to.meta.page > from.meta.page ? 'slide-next' : 'slide-prev'
      }
    },
    quoteTotal: {
      handler () {
        if (this.getSelectedRatesIds.length >= 1 && this.couponIds.length) {
          this.updateChargebeeEstimate()
        }
      },
      deep: true,
    },
  },
  mounted () {
    if (this.getSelectedRatesIds.length >= 1 && this.quoteTotal && this.couponIds.length) this.updateChargebeeEstimate()
  },
  methods: {
    handleSetUpCover () {
      if (this.shouldDisableButton) return
      this.$router.push('/checkout/personal')
    },
    updateChargebeeEstimate () {
      clearTimeout(this.debounceTimeout)
      this.debounceTimeout = setTimeout(() => {
        this.loading = true
        const products = []
        this.getProducts.forEach((product) => {
          let total = 0
          const newObject = { id: '', amount_ex_vat: '' }
          newObject.id = product.agencyProduct.name.replace(/( - )|(\s+)/g, '-')
          product.rates.forEach((rate) => {
            total += rate.loading ? Number(rate.loading) + Number(rate.basePremium) : Number(rate.basePremium)
          })
          newObject.amount_ex_vat = parseInt(total * 100, 10)
          products.push(newObject)
        })

        if (this.$store.getters.getFee) {
          products.push({
            id: 'fee',
            amount_ex_vat: this.$store.getters.getFee * 100,
          })
        }

        this.productsForCoupon = products
        this.getDicountedTotal()
      }, 500)
    },
    getDicountedTotal () {
      const payload = {
        products: this.productsForCoupon,
        couponId: this.couponIds,
        premises: this.premises[0],
      }

      const payloadString = qs.stringify(payload)

      axiosChargebee
        .get(`api/estimate?${payloadString}`)
        .then((response) => {
          if (response.data.invoice_estimate) {
            this.chargebeeEstimate = response.data.invoice_estimate
            this.loading = false
          }
        })
        .catch(() => {
          this.loading = false
        })
        .catch(() => {
          this.$router.push('/404')
        })
    },
    getStatements () {
      if (this.shouldDisableButton) return
      this.loading = true
      let products = []
      if (!this.$store.state.portal) {
        products = this.getProducts
      } else {
        products = this.getProducts.reduce((acc, product) => {
          const p = { ...product }
          p.rates = p.rates.filter(rate => !this.getAccountActiveRates.includes(rate.key))
          if (p.rates.length > 0) acc.push(product)
          return acc
        }, [])
      }

      axiosRatingEngine
        .post('api/statement-of-facts', {
          products,
          overrides: {
            isValidBusiness: this.$store.getters.getIsValidBusiness,
            businessType: this.$store.getters.businessType,
          },
        })
        .then((response) => {
          this.$store.dispatch('updateStatements', response.data).then(() => {
            this.loading = false
            // Go to first statement by using index 0's id
            this.$router.push(`/statements/${response.data[0].id}`)
          })
        })
        .catch(() => {
          this.$router.push('/404')
        })
    },
    async commitRate () {
      this.loading = true

      await this.$store.dispatch('updateQuote', this.$store.getters.getEstimate)

      this.loading = false

      this.$router.push('/products')
    },
    async portalCheckout () {
      try {
        await axiosQuoteApi.post(`/quote/${this.$store.state.quoteId}/convert_no_payment`, {
          accountReference: this.$store.state.portal.account.reference,
        })
        this.$router.push({ name: 'quote-successful' })
      } catch {
        this.$router.push('/404')
      }
    },
  },
}
</script>

<style>
.footer-view {
  display: grid;
  grid-template: 'main';
}

.footer-view > :first-child {
  z-index: 1;
}

.footer-view > * {
  grid-area: main;
}

.inner-footer {
  @apply relative flex justify-center bg-grey-150;
}

@media (max-width: 320px) {
  .products-footer {
    @apply justify-end;
  }
}

.center-v {
  top: 50%;
  transform: translateY(-50%);
}

.footer-background {
  padding: 8px 14px;
}

.btn.footer-button {
  min-width: 100px;

  @apply font-bold rounded-full py-2;
}

@media (min-width: 560px) {
  .btn.footer-button {
    min-width: 150px;

    @apply text-base py-3;
  }
}
</style>
